import { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router'
import type { WellPage_WellCulture } from '~/api/backend/pages/wellPageAPI'
import {
  type ImageFilters,
  ImageManipulationControls,
} from '~/components/ImageManipulationControls'
import { RepresentativeImages } from '~/components/RepresentativeImages'
import Tabs from '~/components/Tabs'
import { useFeatureFlag } from '~/core/featureFlags'
import WellMetadataSidebar from './WellMetadataSidebar'

export interface WellTabbedSidebarProps {
  culture: WellPage_WellCulture
  panAndZoom: boolean
  stitchImage: boolean
  imageFilters: ImageFilters
  onImageFiltersChange: (filters: ImageFilters) => void
  onResetFilters: () => void
  onShareURL: () => Promise<boolean>
  cultureLineageEnabled?: boolean
  onViewLineageClick?: () => void
  onStitchImageChange?: (stitched: boolean) => void
  observationNumber: number
  observationId?: string
  zoomPanState?: { scale: number; positionX: number; positionY: number }
}

export default function WellTabbedSidebar({
  culture,
  panAndZoom,
  stitchImage,
  imageFilters,
  onImageFiltersChange,
  onResetFilters,
  onShareURL,
  cultureLineageEnabled,
  onViewLineageClick,
  onStitchImageChange,
  observationNumber,
  observationId,
  zoomPanState,
}: WellTabbedSidebarProps) {
  const certificateOfAnalysis = useFeatureFlag('certificateOfAnalysis')

  // Get current tab from URL, default to 'metadata'
  const location = useLocation()
  const history = useHistory()
  const urlParams = new URLSearchParams(location.search)
  const urlTab = urlParams.get('tab')

  // Handle tab change by updating URL
  const handleTabChange = useCallback(
    (tabKey: string) => {
      const newParams = new URLSearchParams(location.search)

      // Map internal keys to URL-friendly names
      const urlMapping: { [key: string]: string } = {
        metadata: 'metadata',
        imageControls: 'image-tools',
        savedImages: 'saved-images',
      }

      const urlValue = urlMapping[tabKey] || tabKey

      if (urlValue === 'metadata') {
        // Remove tab param if switching to metadata (default)
        newParams.delete('tab')
      } else {
        newParams.set('tab', urlValue)
      }

      history.replace({ search: newParams.toString() })
    },
    [location.search, history],
  )

  // Switch to saved images tab when an image is saved
  const handleImageSaved = useCallback(() => {
    handleTabChange('savedImages')
  }, [handleTabChange])

  const tabs = useMemo(() => {
    const tabList = [
      {
        key: 'metadata',
        title: 'Metadata',
        panel: (
          <WellMetadataSidebar
            culture={culture}
            inTabs={true}
            cultureLineageEnabled={cultureLineageEnabled}
            onViewLineageClick={onViewLineageClick}
          />
        ),
      },
    ]

    // Show Image Tools tab when panAndZoom is enabled
    if (panAndZoom) {
      tabList.push({
        key: 'imageControls',
        title: 'Image Tools',
        panel: (
          <ImageManipulationControls
            observationNumber={observationNumber}
            filters={imageFilters}
            onFiltersChange={onImageFiltersChange}
            onResetFilters={onResetFilters}
            onShareURL={onShareURL}
            isVisible={true}
            plateBarcode={culture.culturePlate.barcode}
            well={culture.well}
            stitchImage={stitchImage}
            onStitchImageChange={onStitchImageChange}
            cultureId={culture.id}
            observationId={observationId}
            zoomPanState={zoomPanState}
            certificateOfAnalysis={certificateOfAnalysis}
            onImageSaved={handleImageSaved}
          />
        ),
      })
      if (certificateOfAnalysis) {
        tabList.push({
          key: 'savedImages',
          title: 'Saved Images',
          panel: (
            <RepresentativeImages
              observationNumber={observationNumber}
              filters={imageFilters}
              onFiltersChange={onImageFiltersChange}
              onResetFilters={onResetFilters}
              onShareURL={onShareURL}
              isVisible={true}
              plateBarcode={culture.culturePlate.barcode}
              well={culture.well}
              stitchImage={stitchImage}
              onStitchImageChange={onStitchImageChange}
              cultureId={culture.id}
              observationId={observationId}
            />
          ),
        })
      }
    }

    return tabList
  }, [
    culture,
    panAndZoom,
    stitchImage,
    imageFilters,
    onImageFiltersChange,
    onResetFilters,
    onShareURL,
    cultureLineageEnabled,
    onViewLineageClick,
    onStitchImageChange,
    observationNumber,
    observationId,
    zoomPanState,
    handleImageSaved,
    certificateOfAnalysis,
  ])

  // Map URL tab values to internal keys
  const getSelectedTab = () => {
    if (!urlTab) return 'metadata'

    // Map URL-friendly names to internal keys
    const tabMapping: { [key: string]: string } = {
      metadata: 'metadata',
      'image-tools': 'imageControls',
      'saved-images': 'savedImages',
    }

    const mappedTab = tabMapping[urlTab] || urlTab
    const availableKeys = tabs.map(t => t.key)

    // Return the mapped tab if it's available, otherwise default to metadata
    return availableKeys.includes(mappedTab) ? mappedTab : 'metadata'
  }

  const selectedTab = getSelectedTab()

  return (
    <div>
      <Tabs tabs={tabs} selectedTabId={selectedTab} onTabChange={handleTabChange} />
    </div>
  )
}
